import { NavLink } from 'react-router-dom';
import {
  BarChart3,
  Trophy,
  Users,
  UserCheck,
  Database,
  MessageSquare,
  Settings,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAdminFeedbackStats } from '@/sections/users-and-agents/api';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

/**
 * Sidebar order is the current operating priority:
 * Acquisition / People / Competitions / Creators / Game Data / System / Feedback
 */
const NAV_ITEMS: NavItem[] = [
  { to: '/acquisition', label: 'Acquisition', icon: BarChart3 },
  { to: '/people-and-agents', label: 'People', icon: Users },
  { to: '/competitions', label: 'Competitions', icon: Trophy },
  { to: '/creators', label: 'Creators', icon: UserCheck },
  { to: '/game-data', label: 'Game Data', icon: Database },
  { to: '/system', label: 'System', icon: Settings },
  { to: '/feedback', label: 'Feedback', icon: MessageSquare },
];

export function Sidebar() {
  const feedbackStats = useAdminFeedbackStats();
  const unresolvedFeedback = feedbackStats.data?.unresolved_count ?? 0;

  return (
    <aside className="flex min-w-0 shrink-0 flex-col border-b bg-card md:h-screen md:w-[240px] md:border-b-0 md:border-r">
      <div className="hidden h-16 items-center border-b px-6 md:flex">
        <span className="font-barlow text-xl font-bold uppercase tracking-widest">Fantopy</span>
        <span className="ml-2 font-barlow text-xl font-medium uppercase tracking-widest text-muted-foreground">
          Admin
        </span>
      </div>

      <nav aria-label="Admin navigation" className="flex gap-1 overflow-x-auto p-2 md:block md:flex-1 md:space-y-1 md:p-3">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
                'font-barlow uppercase tracking-wide',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )
            }
          >
            <item.icon className="h-4 w-4" />
            <span className="flex-1">{item.label}</span>
            {item.to === '/feedback' && unresolvedFeedback > 0 ? (
              <span className="min-w-5 rounded-full bg-destructive px-1.5 py-0.5 text-center text-[10px] leading-none text-destructive-foreground">
                {unresolvedFeedback > 99 ? '99+' : unresolvedFeedback}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>

      <div className="hidden border-t px-6 py-3 text-xs text-muted-foreground md:block">
        <span>Build {import.meta.env.VITE_BUILD_SHA ?? 'dev'}</span>
      </div>
    </aside>
  );
}
